import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Divider, Box, TextField, MenuItem, Button,
  CircularProgress, ListItem, List, ListItemIcon, ListItemText, IconButton, Alert,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Phone as PhoneIcon,
  Password as PasswordIcon,
  Edit as EditIcon,
  AppRegistration as AppRegistrationIcon,
  Wc as WcIcon,
  CorporateFare as CorporateFareIcon,
  AlternateEmail as AlternateEmailIcon,
  School as SchoolIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import axios from 'axios';

export default function UserProfileEditor({ student, recaptchaRef, enableTicketDownload = false }) {
  const [examCenters, setExamCenters] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [downloading, setDownloading] = useState(false);

  const [formValues, setFormValues] = useState({
    email: student.email || '',
    phone_no: student.phone_no || '',
    gender: student.gender || '',
    examcenter_id: student.examcenter?.id || '',
    grand_father_name: student.grand_father_name || '',
  });

  const [editingFields, setEditingFields] = useState({});

  useEffect(() => {
    const fetchExamCenters = async () => {
      try {
        const res = await axios.get('https://exam.ethernet.edu.et/api/get-examcenters');
        setExamCenters(res.data || []);
      } catch (err) {
        console.error('Failed to fetch exam centers', err);
      }
    };
    fetchExamCenters();
  }, []);

  const toggleEdit = (field) => {
    setEditingFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleFieldChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    setFormValues({
      email: student.email || '',
      phone_no: student.phone_no || '',
      gender: student.gender || '',
      examcenter_id: student.examcenter?.id || '',
      grand_father_name: student.grand_father_name || '',
    });
    setEditingFields({});
  };

  const handleUpdate = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    if (
      (!student.grand_father_name || student.grand_father_name.trim() === '') &&
      (!formValues.grand_father_name || !/^[a-zA-Z ]+$/.test(formValues.grand_father_name))
    ) {
      setError('Grandfather name must contain only letters.');
      setSaving(false);
      return;
    }

    try {
      const token = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset();

      await axios.post('https://exam.ethernet.edu.et/api/update-profile-fields', {
        transID: student.transID,
        email: formValues.email,
        phone_no: formValues.phone_no,
        // gender and examcenter_id updates are disabled
        grand_father_name: formValues.grand_father_name,
        recaptcha_token: token,
      });

      setSuccess('Profile updated successfully!');
      setEditingFields({});
    } catch (err) {
      console.error(err);
      if (err.response?.data?.errors) {
        const messages = Object.values(err.response.data.errors).flat().join(' ');
        setError(messages || 'Update failed.');
      } else {
        setError(err.response?.data?.error || 'Update failed.');
      }
    } finally {
      setSaving(false);
    }
  };

  const downloadAdmissionTicket = async () => {
    setDownloading(true);
    setError('');
    try {
      const response = await axios.post(
        'https://exam.ethernet.edu.et/api/download-ticket',
        { username: student.username },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'EXITEXAM_entrance_ticket.pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError('Error downloading admission ticket. Please try again.');
      console.error('Error downloading admission ticket:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleLogout = () => {
    window.location.href = '/registration';
  };

  const isEditingActive = Object.values(editingFields).some((val) => val);
  const canEditGrandfatherName = !student.grand_father_name || student.grand_father_name.trim() === '';

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        mt: 4, 
        p: 4, 
        maxWidth: 900, 
        mx: 'auto',
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
        <BadgeIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1252a6' }}>
          Candidate Profile Details
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row-reverse' },
        gap: 4,
        alignItems: { xs: 'center', md: 'flex-start' },
      }}>
        {student.profile_picture && (
          <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 0 }, alignSelf: { xs: 'center', md: 'flex-start' } }}>
            <img
              src={`https://exam.ethernet.edu.et/${student.profile_picture}`}
              alt="Profile"
              style={{
                width: 150,
                height: 150,
                objectFit: 'cover',
                border: '1px solid #1976d2',
                boxShadow: '0 0 8px rgba(0,0,0,0.2)',
              }}
            />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 300 }}>
          <List>
            {/* First Name */}
            <ListItem>
              <ListItemIcon><AccountCircleIcon color="primary" /></ListItemIcon>
              <ListItemText primary="First Name" secondary={student.firstname} />
            </ListItem>

            {/* Father Name */}
            <ListItem>
              <ListItemIcon><AccountCircleIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Father Name" secondary={student.lastname} />
            </ListItem>

            {/* Grandfather Name (Editable only if empty) */}
            <ListItem sx={{ display: 'flex', alignItems: 'flex-start' }}>
  <ListItemIcon>
    <AccountCircleIcon color="primary" />
  </ListItemIcon>
  <Box sx={{ flexGrow: 1, ml: 1 }}>
    {canEditGrandfatherName && editingFields.grand_father_name ? (
      <TextField
        size="small"
        value={formValues.grand_father_name}
        onChange={(e) => {
          const value = e.target.value;
          if (/^[a-zA-Z\/ ]*$/.test(value)) {
            handleFieldChange('grand_father_name', value);
          }
        }}
        sx={{ width: 250 }}
        helperText="Please enter your grandfather's name using only letters and '/' if needed"
      />
    ) : (
      <Box>
        <Typography
          variant="body2"
          sx={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          Grand Father Name
          {canEditGrandfatherName && (
            <IconButton
              onClick={() => toggleEdit('grand_father_name')}
              size="small"
              sx={{ p: 0.3, ml: 0.5, color: 'primary.main' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formValues.grand_father_name || (
            <Typography variant="body2" sx={{ color: 'orange', mt: 0.5 }}>
              Please enter your grandfather's name to complete your profile.
            </Typography>
          )}
        </Typography>
      </Box>
    )}
  </Box>
</ListItem>




            {/* Remaining fields (Username, Password, etc.) */}
            <ListItem><ListItemIcon><BadgeIcon color="primary" /></ListItemIcon><ListItemText primary="Username" secondary={student.username} /></ListItem>
            <ListItem><ListItemIcon><PasswordIcon color="primary" /></ListItemIcon><ListItemText primary="Exam Password" secondary={student.password || 'N/A'} /></ListItem>
            <ListItem><ListItemIcon><SchoolIcon color="primary" /></ListItemIcon><ListItemText primary="University" secondary={student.university?.name || 'N/A'} /></ListItem>
            <ListItem><ListItemIcon><CorporateFareIcon color="primary" /></ListItemIcon><ListItemText primary="Exam Topic" secondary={student.department?.name || 'N/A'} /></ListItem>

            {/* Gender - Read Only (Editing Disabled) */}
            <ListItem>
              <ListItemIcon><WcIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Gender" secondary={formValues.gender || 'N/A'} />
            </ListItem>


            {/* Exam Center - Read Only (Editing Disabled) */}
            <ListItem>
              <ListItemIcon><PhoneIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Exam Center" 
                secondary={examCenters.find((c) => c.id === formValues.examcenter_id)?.name || 'N/A'} 
              />
            </ListItem>


            {/* Email */}
            <ListItem sx={{ display: 'flex', alignItems: 'flex-start' }}>
  <ListItemIcon><AlternateEmailIcon color="primary" /></ListItemIcon>
  <Box sx={{ flexGrow: 1, ml: 1 }}>
    {editingFields.email ? (
      <TextField
        size="small"
        value={formValues.email}
        onChange={(e) => handleFieldChange('email', e.target.value)}
        sx={{ width: 250 }}
      />
    ) : (
      <Box>
        <Typography
          variant="body2"
          sx={{ display: 'flex', alignItems: 'center', gap: 15 }}
        >
          Email
          <IconButton
            onClick={() => toggleEdit('email')}
            size="small"
            sx={{ p: 0.3, color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formValues.email || 'N/A'}
        </Typography>
      </Box>
    )}
  </Box>
</ListItem>


            {/* Phone */}
            <ListItem sx={{ display: 'flex', alignItems: 'flex-start' }}>
  <ListItemIcon><PhoneIcon color="primary" /></ListItemIcon>
  <Box sx={{ flexGrow: 1, ml: 1 }}>
    {editingFields.phone_no ? (
      <TextField
        size="small"
        value={formValues.phone_no}
        onChange={(e) => {
          const value = e.target.value;
          if (/^\d*$/.test(value)) {
            handleFieldChange('phone_no', value);
          }
        }}
        sx={{ width: 200 }}
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
      />
    ) : (
      <Box>
        <Typography
          variant="body2"
          sx={{ display: 'flex', alignItems: 'center', gap: 15 }}
        >
          Phone
          <IconButton
            onClick={() => toggleEdit('phone_no')}
            size="small"
            sx={{ p: 0.3, color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formValues.phone_no || 'N/A'}
        </Typography>
      </Box>
    )}
  </Box>
</ListItem>


            <ListItem>
              <ListItemIcon><AppRegistrationIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Registration Status" secondary={student.is_paid ? '✅ Completed' : '❌ Incomplete'} />
            </ListItem>
          </List>
        </Box>
      </Box>
       <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        {enableTicketDownload ? (
          <Button
            variant="contained"
            color="primary"
            onClick={downloadAdmissionTicket}
            disabled={downloading}
            startIcon={downloading ? <CircularProgress size={20} /> : null}
          >
            Download Exam Entry Ticket
          </Button>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              disabled
              sx={{ mb: 1 }}
            >
              Download Exam Entry Ticket
            </Button>
            <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic', mt: 1 }}>
              Your ticket will be available soon
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      {success && (
        <Box sx={{ mt: 3 }}>
          <Alert
            severity="success"
            variant="outlined"
            sx={{ backgroundColor: '#f1fdf7', color: '#2e7d32', borderColor: '#c8e6c9' }}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        </Box>
      )}

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: { xs: 'stretch', sm: 'space-between' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 2 },
        mt: 2 
      }}>
        <Box>
          {!isEditingActive && (
            <Button 
              variant="contained" 
              color="error" 
              onClick={handleLogout}
              fullWidth={{ xs: true, sm: false }}
            >
              Logout
            </Button>
          )}
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          {isEditingActive && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 2 },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button 
                variant="outlined" 
                onClick={handleCancel} 
                disabled={saving}
                fullWidth={{ xs: true, sm: false }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleUpdate} 
                disabled={saving}
                fullWidth={{ xs: true, sm: false }}
              >
                {saving ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
